/**
 * CreateEmployeeContext — app-level controller for the CreateEmployee modal.
 *
 * Lifts the modal out of TeamPage so any page can open it instantly without
 * a route transition. The modal renders once in AppShell, above all routes.
 *
 * Usage:
 *   const { openCreateEmployee } = useCreateEmployee()
 *   openCreateEmployee()                         // blank form
 *   openCreateEmployee({ prefill: { ... } })     // pre-filled (demo mode)
 */

import React, { createContext, useContext, useState, useCallback } from 'react'
import type { CreateEmployeePrefill } from '../components/admin/CreateEmployee'

interface CreateEmployeeContextValue {
  open: boolean
  prefill: CreateEmployeePrefill | undefined
  openCreateEmployee: (opts?: { prefill?: CreateEmployeePrefill }) => void
  closeCreateEmployee: () => void
}

const CreateEmployeeContext = createContext<CreateEmployeeContextValue | null>(null)

export function CreateEmployeeProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  const [prefill, setPrefill] = useState<CreateEmployeePrefill | undefined>()

  const openCreateEmployee = useCallback((opts?: { prefill?: CreateEmployeePrefill }) => {
    setPrefill(opts?.prefill)
    setOpen(true)
  }, [])

  const closeCreateEmployee = useCallback(() => {
    setOpen(false)
    setPrefill(undefined)
  }, [])

  return (
    <CreateEmployeeContext.Provider value={{ open, prefill, openCreateEmployee, closeCreateEmployee }}>
      {children}
    </CreateEmployeeContext.Provider>
  )
}

export function useCreateEmployee() {
  const ctx = useContext(CreateEmployeeContext)
  if (!ctx) throw new Error('useCreateEmployee must be inside CreateEmployeeProvider')
  return ctx
}

/** Fire this after a successful creation so mounted pages can refetch. */
export function dispatchEmployeeCreated() {
  window.dispatchEvent(new CustomEvent('odonboard:employee-created'))
}
